import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAppState, useDispatch } from '../state/AppContext';
import { ADD_SEED_ARTIST, REMOVE_SEED_ARTIST, START_GENERATING, SET_ERROR, CONSUME_LIBRARY_VIEW } from '../state/actions';
import { MIN_SEED_ARTISTS } from '../utils/constants';
import { generateRecommendations } from '../engine/recommendationEngine';
import { SET_LOADING_PROGRESS, SET_GALAXY_DATA } from '../state/actions';
import { useAuth } from '../state/AuthContext';
import Header from './Header';
import ArtistSearch from './ArtistSearch';
import ArtistChipList from './ArtistChipList';
import GenerateButton from './GenerateButton';
import SavedMapsSection from './SavedMapsSection';
import MyUniverseSection from './MyUniverseSection';
import FavoritesSection from './FavoritesSection';
import DiscoveredSection from './DiscoveredSection';
import AchievementsSection from './AchievementsSection';
import FriendsSection from './FriendsSection';
import NewReleasesSection from './NewReleasesSection';
import ImportLocalMapsPrompt from './ImportLocalMapsPrompt';
import '../styles/landing.css';

function LandingPage() {
  const { seedArtists, returnTab, libraryView: libraryViewTarget } = useAppState();
  const dispatch = useDispatch();
  const { user, isLoading, favorites, discoveredArtists, dislikes, knownArtists, universeData } = useAuth();
  const [activeTab, setActiveTabRaw] = useState(returnTab || 'map');
  // Library tab inner view: 'home' chooser → 'artists' (discovered/favorited)
  // or 'achievements'. Seeded from a one-shot deep-link target (e.g. tapping
  // "View achievements" on a milestone popup) and otherwise starts at 'home'.
  const [libraryView, setLibraryView] = useState(libraryViewTarget || 'home');
  const [librarySubTab, setLibrarySubTab] = useState('discovered');
  const [libraryFilter, setLibraryFilter] = useState('');
  const contentRef = useRef(null);

  const setActiveTab = useCallback((tab) => {
    setActiveTabRaw(tab);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, []);

  // Consume the one-shot library deep-link target so it doesn't stick around
  // and reopen the achievements view on a later, unrelated visit.
  useEffect(() => {
    if (libraryViewTarget) {
      dispatch({ type: CONSUME_LIBRARY_VIEW });
    }
  }, [libraryViewTarget, dispatch]);

  const isReturningUser = isLoading || !!user;

  const selectedIds = useMemo(
    () => new Set(seedArtists.map((a) => a.id)),
    [seedArtists]
  );

  // Total artists the user has marked via any action (known, favorited,
  // disliked, discovered) — deduplicated across overlap. Surfaced as the
  // header's hero stat so every artist action grows a visible counter.
  // Only computed for signed-in users; signed-out visitors keep the seed
  // counter so the search-and-add flow still has a visible chip count.
  const exploredCount = useMemo(() => {
    if (!user) return undefined;
    const set = new Set();
    for (const k of knownArtists) set.add(k.artistName);
    for (const f of favorites) set.add(f.artistName);
    for (const d of dislikes) set.add(d.artistName);
    for (const dd of discoveredArtists) set.add(dd.artistName);
    return set.size;
  }, [user, knownArtists, favorites, dislikes, discoveredArtists]);

  const universeArtistCount = favorites.length + discoveredArtists.length;
  const universeRecCount = useMemo(() => {
    if (!universeData?.clusters) return 0;
    return universeData.clusters.reduce(
      (sum, c) => sum + (c.recommendations?.length || 0),
      0
    );
  }, [universeData]);

  const handleSelect = useCallback(
    (artist) => {
      dispatch({ type: ADD_SEED_ARTIST, payload: artist });
    },
    [dispatch]
  );

  const handleRemove = useCallback(
    (artistId) => {
      dispatch({ type: REMOVE_SEED_ARTIST, payload: artistId });
    },
    [dispatch]
  );

  const handleGenerate = useCallback(async () => {
    if (seedArtists.length < MIN_SEED_ARTISTS) return;

    dispatch({ type: START_GENERATING });

    try {
      const galaxyData = await generateRecommendations(
        seedArtists,
        (progress) => {
          dispatch({ type: SET_LOADING_PROGRESS, payload: progress });
        }
      );
      dispatch({ type: SET_GALAXY_DATA, payload: galaxyData });
    } catch (err) {
      console.error('Generation failed:', err);
      dispatch({
        type: SET_ERROR,
        payload: err.message || 'Failed to generate recommendations. Please try again.',
      });
    }
  }, [seedArtists, dispatch]);

  return (
    <div className="landing-page">
      <Header artistCount={seedArtists.length} exploredCount={exploredCount} />

      <div className="landing-content" ref={contentRef}>
        {/* Map Tab — always mounted so saved maps don't reload */}
        <div className="tab-content" style={activeTab !== 'map' ? { display: 'none' } : undefined}>
          <div className={`landing-hero${isReturningUser ? ' landing-hero-compact' : ''}`}>
            <h2 className="hero-title">
              Discover Your Music
              <br />
              <span className="hero-accent">Universe</span>
            </h2>
            {!isReturningUser && (
              <>
                <p className="hero-tagline">Find your new favorite music.</p>
                <p className="hero-description">
                  Add artists you love and we'll map the galaxy of music that connects them,
                  revealing new artists in the spaces between your favorites.
                </p>
              </>
            )}
          </div>

          <div className="landing-input-section">
            <ArtistSearch onSelect={handleSelect} selectedIds={selectedIds} artistCount={seedArtists.length} />
            <ArtistChipList artists={seedArtists} onRemove={handleRemove} />
            <GenerateButton
              artistCount={seedArtists.length}
              onClick={handleGenerate}
            />
          </div>

          {!isLoading && (
            <>
              <SavedMapsSection />
              <ImportLocalMapsPrompt />
            </>
          )}
        </div>

        {/* Universe Tab — always mounted so compute continues across tabs */}
        {!isLoading && (
          <div className="tab-content" style={activeTab !== 'universe' ? { display: 'none' } : undefined}>
            <h3 className="tab-section-header">
              {user?.displayName ? `${user.displayName}\u2019s Universe` : 'My Universe'}
              {universeArtistCount > 0 && (
                <span className="section-count">{universeArtistCount} Artists</span>
              )}
              {universeArtistCount > 0 && (
                <span className="section-count">{universeRecCount} Recs</span>
              )}
            </h3>
            <MyUniverseSection visible={activeTab === 'universe'} />
            <NewReleasesSection showHeader />
          </div>
        )}

        {/* Friends Tab */}
        {activeTab === 'friends' && !isLoading && (
          <div className="tab-content">
            <FriendsSection />
          </div>
        )}

        {/* Library Tab */}
        {activeTab === 'library' && !isLoading && (
          <div className="tab-content">
            {/* Home chooser — pick Artists or Achievements */}
            {libraryView === 'home' && (
              <div className="library-home">
                <button
                  className="library-home-card"
                  onClick={() => { setLibraryView('artists'); setLibrarySubTab('discovered'); setLibraryFilter(''); }}
                >
                  <span className="library-home-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="24" height="24">
                      <circle cx="9" cy="8" r="3.2" />
                      <path d="M3.5 19v-1.2A3.8 3.8 0 0 1 7.3 14h3.4a3.8 3.8 0 0 1 3.8 3.8V19" />
                      <path d="M16 5.2a3.2 3.2 0 0 1 0 6" />
                      <path d="M17.5 14.2a3.8 3.8 0 0 1 3 3.6V19" />
                    </svg>
                  </span>
                  <span className="library-home-body">
                    <span className="library-home-title">Artists</span>
                    <span className="library-home-sub">
                      {discoveredArtists.length + favorites.length > 0
                        ? `${discoveredArtists.length} discovered · ${favorites.length} favorited`
                        : 'Your discovered & favorite artists'}
                    </span>
                  </span>
                </button>
                <button
                  className="library-home-card"
                  onClick={() => setLibraryView('achievements')}
                >
                  <span className="library-home-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                      <path d="M12 2.5l2.6 6 6.5.5-5 4.2 1.6 6.3L12 16.2 6.3 19.5l1.6-6.3-5-4.2 6.5-.5z" />
                    </svg>
                  </span>
                  <span className="library-home-body">
                    <span className="library-home-title">Achievements</span>
                    <span className="library-home-sub">Milestones you've unlocked</span>
                  </span>
                </button>
              </div>
            )}

            {/* Artists view — existing discovered / favorited browser */}
            {libraryView === 'artists' && (
              <>
                <button className="library-back" onClick={() => setLibraryView('home')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  Library
                </button>
                <div className="library-sub-tabs">
                  <button
                    className={`library-sub-tab${librarySubTab === 'discovered' ? ' active' : ''}`}
                    onClick={() => { setLibrarySubTab('discovered'); setLibraryFilter(''); }}
                  >
                    Discovered
                    {discoveredArtists.length > 0 && <span className="section-count">{discoveredArtists.length}</span>}
                  </button>
                  <button
                    className={`library-sub-tab${librarySubTab === 'favorited' ? ' active' : ''}`}
                    onClick={() => { setLibrarySubTab('favorited'); setLibraryFilter(''); }}
                  >
                    Favorited
                    {favorites.length > 0 && <span className="section-count">{favorites.length}</span>}
                  </button>
                </div>
                <div className="library-filter">
                  <svg className="library-filter-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    className="library-filter-input"
                    type="text"
                    placeholder={librarySubTab === 'discovered' ? 'Search discovered artists...' : 'Search favorite artists...'}
                    value={libraryFilter}
                    onChange={(e) => setLibraryFilter(e.target.value)}
                  />
                </div>
                {librarySubTab === 'discovered' && <DiscoveredSection filterQuery={libraryFilter} />}
                {librarySubTab === 'favorited' && <FavoritesSection filterQuery={libraryFilter} />}
              </>
            )}

            {/* Achievements view */}
            {libraryView === 'achievements' && (
              <>
                <button className="library-back" onClick={() => setLibraryView('home')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  Library
                </button>
                <AchievementsSection />
              </>
            )}
          </div>
        )}
      </div>

      {/* Bottom Tab Bar */}
      <nav className="landing-tab-bar">
        <button
          className={`landing-tab${activeTab === 'map' ? ' active' : ''}`}
          onClick={() => setActiveTab('map')}
        >
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="12" cy="12" r="2.5" fill="currentColor" />
            <path d="M12 3c3 3 4.5 5.5 4.5 9s-1.5 6-4.5 9" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
            <path d="M12 3c-3 3-4.5 5.5-4.5 9s1.5 6 4.5 9" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
          </svg>
          <span>Map</span>
        </button>
        <button
          className={`landing-tab${activeTab === 'universe' ? ' active' : ''}`}
          onClick={() => setActiveTab('universe')}
        >
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <circle cx="12" cy="12" r="3" fill="currentColor" />
            <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1" opacity="0.4" />
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="0.8" opacity="0.25" />
            <circle cx="5" cy="10" r="1.2" fill="currentColor" opacity="0.5" />
            <circle cx="18" cy="8" r="1" fill="currentColor" opacity="0.4" />
            <circle cx="16" cy="17" r="0.8" fill="currentColor" opacity="0.35" />
          </svg>
          <span>Universe</span>
        </button>
        <button
          className={`landing-tab${activeTab === 'friends' ? ' active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span>Friends</span>
        </button>
        <button
          className={`landing-tab${activeTab === 'library' ? ' active' : ''}`}
          onClick={() => setActiveTab('library')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            <line x1="9" y1="7" x2="15" y2="7" />
            <line x1="9" y1="11" x2="13" y2="11" />
          </svg>
          <span>Library</span>
        </button>
      </nav>

      <div className="landing-bg-stars" aria-hidden="true" />
    </div>
  );
}

export default LandingPage;
